import { Collapse } from "@mui/material";
import { RouteRequestResult } from "../../generated/gpb/api/route";
import { useGetRouteMetricsAndInstructionsQuery, useSetSelectedRouteMutation } from "../../services/routing";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { getSummaryRouteMetrics } from "../metric/RouteUtils";
import { StyledMultistartCard, TakeRouteButton } from "./MutistartDrawerStyles";
import RouteSummary from "./RouteSummary";
import RouteHeader from "./RouteHeader";
import { selectMultistartRoute, setSelectedRoute } from "../../features/riskViewer/riskViewerSlice";
import {
    multistartToTripResults,
    updateTripRouteId,
    useTripIdAndRouteRequestIndex
} from "../../features/Drawer/drawerSlice";
import { useDispatch } from "react-redux";
import React, { useState } from "react";
import SeeRouteDetailsButton from "./SeeRouteDetailsButton";
import { MULTISTART_SELECT_BUTTON_TEXT } from "../UserVisibleStrings";
import { RouteMetricsContent } from "../metric/RouteMetricsDialog";


type MultistartCardProps = {
    result: RouteRequestResult,
    isSelected: boolean,
    showButtons: boolean, // if multistart complete, show button, if just multistart eligible, hide buttons
    label: string | undefined,
};
/**
 * MultistartCard component to display trip details and allow the user to select a route
 *
 * @param {MultistartCardProps} props - Props containing the result, the selected status of the card, and its index
 * @returns {JSX.Element} The rendered TripCard component.
 */
export default function MultistartCard( { result, isSelected, showButtons, label }: Readonly<MultistartCardProps> )
{
    const routeId = result.routeId;
    const dispatch = useDispatch();
    const [ showDetails, setShowDetails ] = useState( false );
    const tripInfo = useTripIdAndRouteRequestIndex();
    const tripId = tripInfo?.tripId;


    const {
        data: metricsAndInstructions,
    } = useGetRouteMetricsAndInstructionsQuery( routeId?.hexString ?? skipToken );

    const [ chooseRoute ] = useSetSelectedRouteMutation();

    const metricsSummary = metricsAndInstructions?.metrics !== undefined
        ? getSummaryRouteMetrics( metricsAndInstructions.metrics, undefined )
        : undefined;

    const handleViewVariationClick = () =>
    {
        dispatch( setSelectedRoute( routeId ) );
        dispatch( updateTripRouteId( { routeId: routeId } ) );
    };

    const handleChooseRoute = async () =>
    {
        dispatch( multistartToTripResults( { routeId: routeId } ) );
        dispatch( selectMultistartRoute( routeId ) );
        if ( tripId !== undefined && routeId !== undefined )
        {
            await chooseRoute( { tripId: tripId?.hexString, routeId: routeId?.hexString } );
        }
    };

    return (
        <StyledMultistartCard isSelected = {isSelected} onClick = {handleViewVariationClick}>
            {metricsSummary !== undefined &&
                <RouteHeader
                    label = {label}
                    routeResult = {result}
                    maxRisk = {metricsSummary?.maxImpactObserved}
                />}
            {metricsSummary !== undefined && <RouteSummary metricsSummary = {metricsSummary}/>}

            {isSelected && metricsAndInstructions !== undefined && (
                <React.Fragment>
                    <Collapse in = {showDetails} timeout = "auto" unmountOnExit>
                        <RouteMetricsContent routeMetrics = {metricsAndInstructions.metrics} showTemps/>
                    </Collapse>

                    {showButtons &&
                        <TakeRouteButton
                            variant = "contained"
                            sx = {{ textTransform: "none" }}
                            onClick = {handleChooseRoute}
                        >
                            {MULTISTART_SELECT_BUTTON_TEXT}
                        </TakeRouteButton>}

                    <SeeRouteDetailsButton
                        open = {showDetails}
                        onClick = {( e ) =>
                        {
                            e.stopPropagation();
                            const newVal = !showDetails;
                            setShowDetails( newVal );
                        }}
                    />

                </React.Fragment>
            )}
        </StyledMultistartCard>
    );
}


